import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { Table, Form, Spinner } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import './DataTable.scss';

interface Column {
  header: string;
  accessorKey?: string;
  id?: string;
  cell?: (props: { row: { original: any; index?: number } }) => React.ReactNode;
}

interface DataTableProps {
  columns: Column[];
  queryFn: (
    page: number,
    search: string,
    sortBy?: string,
    sortOrder?: string
  ) => Promise<{ data: any[]; total: number }>;
  queryKey: unknown[];
  pageSize?: number;
  searchContainer?: HTMLElement | null;
  onVisibleDataChange?: (data: any[], loading: boolean) => void;
}

const DataTable = forwardRef<{ refetch: () => void; getSearchTerm?: () => string }, DataTableProps>(
  ({ columns, queryFn, queryKey, pageSize = 25, onVisibleDataChange, searchContainer }, ref) => {
    const [data, setData]               = useState<any[]>([]);
    const [loading, setLoading]         = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm]   = useState('');
    const [sortBy, setSortBy]           = useState('id');
    const [sortOrder, setSortOrder]     = useState<'asc' | 'desc'>('desc');

    useEffect(() => {
      onVisibleDataChange?.(data, loading);
    }, [data, loading, onVisibleDataChange]);

    const totalPages = Math.ceil(totalRecords / pageSize) || 1;

    const visiblePageNumbers = (() => {
      if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);

      const pages: number[] = [];
      const start = Math.max(1, currentPage - 2);
      const end = Math.min(totalPages, start + 4);

      for (let i = start; i <= end; i += 1) {
        pages.push(i);
      }

      return pages;
    })();

    // ── Keep latest values accessible inside callbacks without re-creating them ──
    const stateRef = useRef({ currentPage, searchTerm, sortBy, sortOrder });
    useEffect(() => {
      stateRef.current = { currentPage, searchTerm, sortBy, sortOrder };
    });

    // ── Debounce timer ────────────────────────────────────────────────────────
    const debounceTimer = useRef<ReturnType<typeof setTimeout>>();

    // ── Single fetch function ─────────────────────────────────────────────────
    const fetchData = useRef(
      async (page: number, search: string, sort: string, order: 'asc' | 'desc') => {
        setLoading(true);
        try {
          const result = await queryFn(page, search, sort, order);
          setData(result.data ?? []);
          setTotalRecords(result.total ?? 0);
        } catch (err) {
          console.error('DataTable fetch error:', err);
          setData([]);
          setTotalRecords(0);
        } finally {
          setLoading(false);
        }
      }
    );

    // Keep queryFn reference fresh without causing re-renders
    const queryFnRef = useRef(queryFn);
    useEffect(() => { queryFnRef.current = queryFn; }, [queryFn]);
    // Patch fetchData to always use the latest queryFn
    useEffect(() => {
      fetchData.current = async (
        page: number,
        search: string,
        sort: string,
        order: 'asc' | 'desc'
      ) => {
        setLoading(true);
        try {
          const result = await queryFnRef.current(page, search, sort, order);
          setData(result.data ?? []);
          setTotalRecords(result.total ?? 0);
        } catch (err) {
          console.error('DataTable fetch error:', err);
          setData([]);
          setTotalRecords(0);
        } finally {
          setLoading(false);
        }
      };
    }, []);

    // ── Initial load + re-fetch when page / sort changes ─────────────────────
    useEffect(() => {
      fetchData.current(currentPage, searchTerm, sortBy, sortOrder);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, sortBy, sortOrder]);
    // Note: searchTerm is intentionally excluded — search triggers fetch via
    // the debounced handler below, which also resets page to 1.

    // ── Re-fetch when queryKey changes (e.g. date filter applied) ─────────────
    // We skip the very first render (handled by the effect above).
    const isFirstRender = useRef(true);
    useEffect(() => {
      if (isFirstRender.current) { isFirstRender.current = false; return; }
      setCurrentPage(1);
      const { searchTerm: s, sortBy: sb, sortOrder: so } = stateRef.current;
      fetchData.current(1, s, sb, so);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(queryKey)]);

    // ── Expose refetch to parent via ref ──────────────────────────────────────
    useImperativeHandle(ref, () => ({
      getSearchTerm: () => stateRef.current.searchTerm,
      refetch: () => {
        const { currentPage: p, searchTerm: s, sortBy: sb, sortOrder: so } =
          stateRef.current;
        fetchData.current(p, s, sb, so);
      },
    }));

    // ── Search input handler with debounce ───────────────────────────────────
    const handleSearch = (value: string) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        setSearchTerm(value);
        setCurrentPage(1);
        // Read sort from ref so we always use current values
        const { sortBy: sb, sortOrder: so } = stateRef.current;
        fetchData.current(1, value, sb, so);
      }, 300);
    };

    // ── Sort handler ──────────────────────────────────────────────────────────
    const handleSort = (columnKey: string) => {
      if (sortBy === columnKey) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(columnKey);
        setSortOrder('asc');
      }
      setCurrentPage(1);
    };

    const getSortIcon = (columnKey: string) => {
      if (sortBy !== columnKey) return <span className="sort-icon">↕</span>;
      return (
        <span className="sort-icon active">
          {sortOrder === 'asc' ? '↑' : '↓'}
        </span>
      );
    };

    const searchInput = (
        <div className="dt-search">
          <Form.Control
            type="search"
            placeholder="Search..."
            onChange={(e) => handleSearch(e.target.value)}
            className="form-control-sm"
          />
        </div>
    );

    // ── Render ────────────────────────────────────────────────────────────────
    return (
      <div className="compact-datatable">
        {/* Search */}
        {searchContainer === undefined ? searchInput : searchContainer && createPortal(searchInput, searchContainer)}

        {/* Table */}
        <div className="table-responsive">
          <Table striped bordered hover size="md" className="table-md mb-1">
            <thead>
              <tr>
                {columns.map((col, idx) => (
                  <th
                    key={col.id || col.accessorKey || idx}
                    onClick={() => col.accessorKey && handleSort(col.accessorKey)}
                    style={{ cursor: col.accessorKey ? 'pointer' : 'default' }}
                  >
                    {col.header}
                    {col.accessorKey && (
                      <span className="ms-1">{getSortIcon(col.accessorKey)}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-4">
                    <Spinner animation="border" size="sm" role="status" />
                    <span className="ms-2 text-muted" style={{ fontSize: '0.85rem' }}>
                      Loading…
                    </span>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-3 text-muted">
                    No records found
                  </td>
                </tr>
              ) : (
                data.map((row, rowIdx) => (
                  <tr key={row.id ?? rowIdx}>
                    {columns.map((col, colIdx) => (
                      <td key={col.id || col.accessorKey || colIdx}>
                        {col.cell
                          ? col.cell({ row: { original: row, index: rowIdx } })
                          : col.accessorKey
                          ? row[col.accessorKey]
                          : null}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-end align-items-center dt-pagination gap-2">
            <span className="text-muted small me-2">
              Page {currentPage} of {totalPages}
            </span>
            <div className="btn-group btn-group-sm">
              <button
                className="btn btn-outline-primary"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1 || loading}
                aria-label="Previous page"
              >
                Pre
              </button>

              {visiblePageNumbers.map((pageNo) => (
                <button
                  key={pageNo}
                  className={`btn ${pageNo === currentPage ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setCurrentPage(pageNo)}
                  disabled={loading}
                  aria-label={`Go to page ${pageNo}`}
                >
                  {pageNo}
                </button>
              ))}

              <button
                className="btn btn-outline-primary"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages || loading}
                aria-label="Next page"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
);

DataTable.displayName = 'DataTable';
export default DataTable;
